import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';
import { makeModeNameHumanized } from 'darwin_core/utils/helpers.js';

import BackLink from '../navigation/BackLink.jsx';
import { zonePath } from '../navigation/paths.js';
import { useZoneRouteContext } from '../navigation/ZoneRoute.jsx';
import { useZoneControls } from '../controls/useZoneControls.js';
import { isOverridden } from '../controls/overrideCopy.js';
import CancelOverride from '../controls/CancelOverride.jsx';
import { ZoneHolidayBanner } from '../components/HolidayBanner.jsx';
import { OfflineHelp, useZoneOfflineHelp } from '../components/OfflineHelp.jsx';
import useScreen from '../hooks/useScreen.js';

// The mode-select screen reached from a zone's "Heating Status" action — a web
// port of the app's ModeSelect / HotWaterModeSelect. It offers the zone's modes as
// selectable cards (heating: Auto / Manual / Off; hot water: On / Off, plus Auto
// when the cylinder is timed or on a boiler), each with the app's description.
//
// While the zone is in an override the modes are LOCKED and an override section is
// shown at the top carrying the single cancel-override exit (the same CancelOverride
// used on the zone screen). Selecting a mode dispatches setMode — darwin_core owns
// the side-effects (Off → frost, Auto → scheduled setpoint) — and returns to the
// zone screen.
//
// Copy is taken VERBATIM from the app's ModeSelect / HotWaterModeSelect and varies by
// device family: `isHeatPump` (the app's isHaloAir / a Halo Air home) vs a boiler.
const heatingModes = (isHeatPump) => [
  {
    mode: MODES.Auto,
    label: 'Auto',
    description: isHeatPump
      ? 'Your heating will run to pre-set scheduled times and temperatures'
      : 'Your heating times and temperatures run to your set schedule',
  },
  {
    mode: MODES.Manual,
    label: 'Manual',
    description: isHeatPump
      ? 'You control the temperature by manually altering the required room temperature, there are no set schedules'
      : 'Your schedule is inactive and temperature is controlled by the dial',
  },
  {
    mode: MODES.Off,
    label: 'Off',
    description: isHeatPump
      ? 'Your heating will be permanently off and will only activate for frost protection'
      : 'Your heating is permanently off, frost protection is active',
  },
];

const dhwOn = (isHeatPump) => ({
  mode: MODES.On,
  label: 'On',
  description: isHeatPump
    ? 'Your heat pump will automatically top up your hot water cylinder when the temperature drops below the cylinder thermostat. This ensures you always have hot water available.'
    : 'Your boiler will automatically top up your hot water cylinder when the temperature drops below the cylinder thermostat.',
});
const dhwOff = (isHeatPump) => ({
  mode: MODES.Off,
  label: 'Off',
  description: isHeatPump
    ? 'Your hot water will be permanently off, your heat pump will not heat your hot water cylinder.'
    : 'Your hot water will be permanently off, your boiler will not heat your hot water cylinder.',
});
const dhwAuto = (isHeatPump) => ({
  mode: MODES.Auto,
  label: 'Auto',
  description: isHeatPump
    ? 'Your hot water cylinder will be heated during the times that you specify, timed hot water may not provide optimum efficiency for your heat pump.'
    : 'Your hot water cylinder will heat up according to the times you set in your schedule',
});

export default function HeatingStatusScreen() {
  const { homeId, zoneId } = useZoneRouteContext();
  const navigate = useNavigate();
  const { online, inHoliday, sendable, actions } = useZoneControls(
    homeId,
    zoneId,
  );

  const name = useSelector((s) =>
    selectors.getPrtFriendlyName(s, homeId, zoneId),
  );
  const mode = useSelector((s) => selectors.getMode(s, homeId, zoneId));
  const previousMode = useSelector((s) =>
    selectors.getPreviousMode(s, homeId, zoneId),
  );
  const isDhw = useSelector((s) => selectors.isDhw(s, homeId, zoneId));
  const isHpi = useSelector((s) => selectors.isHpi(s, homeId, zoneId));
  const timedDhw = useSelector((s) => selectors.getTimedDhw(s, homeId, zoneId));
  const offlineHelp = useZoneOfflineHelp(homeId, zoneId);

  const overridden = isOverridden(mode, isDhw);
  // setMode no-ops in holiday / offline, so lock the cards then — not only during
  // an override — so a tap can't look like it worked when darwin_core silently
  // dropped it. A command still in flight does NOT lock them: darwin_core takes a
  // new command on top of one still out, and the Zone header spinner is what
  // tells the user a change is still on its way. Holiday is explained by the
  // ZoneHolidayBanner under the header (with the way out); an unreachable zone by
  // the offline help below it, and our own dropped socket by the hint beside it.
  const locked = overridden || inHoliday || !online || !sendable;
  // The app switches its copy on isHaloAir (a Halo Air / heat-pump home) vs a boiler;
  // isHpi is the per-zone equivalent.
  const isHeatPump = isHpi;
  // DHW Auto is only meaningful when the cylinder runs to a schedule (timed) or it
  // is a boiler (non-HPI) — matches the app's showAuto.
  const options = isDhw
    ? [
        dhwOn(isHeatPump),
        dhwOff(isHeatPump),
        ...(timedDhw || !isHpi ? [dhwAuto(isHeatPump)] : []),
      ]
    : heatingModes(isHeatPump);

  const heading = isDhw ? 'Hot Water status' : 'Heating status';
  const headingRef = useScreen(heading);
  const subheading = isDhw
    ? "Change your device's hot water status"
    : "Change your device's heating status";

  const choose = (selected) => {
    actions.setMode(selected);
    navigate(zonePath(homeId, zoneId));
  };

  return (
    <main className="app-shell screen">
      <header className="screen__header">
        <BackLink to={zonePath(homeId, zoneId)} label={name || 'Zone'} />
        <h1 id="main-content" tabIndex={-1} ref={headingRef}>
          {heading}
        </h1>
        <p className="screen__sub">{subheading}</p>
      </header>

      <ZoneHolidayBanner
        homeId={homeId}
        zoneId={zoneId}
        detail={`Cancel Holiday to change the ${
          isDhw ? 'hot water' : 'heating'
        } status.`}
      />

      {overridden && (
        <section className="mode-override">
          <h2 className="mode-override__title">
            {!isDhw && mode === MODES.DSRActive ? 'DSR Program' : 'Override'}
          </h2>
          <p className="mode-override__desc">
            {isDhw
              ? 'By canceling override you will be taken back to Auto mode'
              : `By cancelling the ${makeModeNameHumanized(mode)} you will be taken back to ${
                  mode === MODES.DSRActive
                    ? makeModeNameHumanized(previousMode)
                    : 'Auto'
                } mode.`}
          </p>
          <CancelOverride homeId={homeId} zoneId={zoneId} />
        </section>
      )}

      {/* Why the cards below are locked when the zone cannot be reached. The
          device's cause is stated in the app's words rather than a hint of our
          own, and it stands whatever else is true of the zone — an override or a
          holiday does not change which link is down. Our own dropped socket keeps
          its separate line, shown only while the device is otherwise reachable so
          the screen never offers two competing causes. */}
      {offlineHelp.show ? (
        <OfflineHelp {...offlineHelp} />
      ) : (
        !overridden &&
        !sendable && (
          <p className="mode-hint">
            You appear to be offline — reconnect to change the{' '}
            {isDhw ? 'hot water' : 'heating'} status.
          </p>
        )
      )}

      <ul className="mode-list">
        {options.map(({ mode: optionMode, label, description }) => (
          <li key={optionMode}>
            <button
              type="button"
              className="mode-card"
              aria-pressed={mode === optionMode}
              disabled={locked}
              onClick={() => choose(optionMode)}
            >
              <span className="mode-card__title">{label}</span>
              <span className="mode-card__desc">{description}</span>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}
